import { styled } from "@mui-wrapper";
import { EntityCellParams } from "@components/common/data-grid/adm-data-grid-types";
import { DateTimeInput } from "@custom-types/base-types";
import { Tooltip, Text } from "@flat-ui";
import {
  GridOverlay,
  GridRow,
  GridRowProps,
  RowPropsOverrides,
} from "@mui/x-data-grid-pro";
import { DateTimeUtils } from "@stellar/utils";
import { devFeaturesStateSelector } from "@store/dev-features/dev-features-slice";
import { useAppSelector } from "@store/store-helper";
import { withEllipsisMultiline } from "@styles/common-styles";

/** CSS class to only show an element when the mouse is hovering over its row or the row has focus. */
export const SHOW_ON_HOVER_CLASS = "show-on-hover";

/** CSS Custom row to show some elements on hover */
export const DataGridCustomRow = styled(
  (props: GridRowProps & RowPropsOverrides) => <GridRow {...props} />
)({
  [`&:hover .${SHOW_ON_HOVER_CLASS}, &:focus-within .${SHOW_ON_HOVER_CLASS}`]: {
    // Use !important to make sure that the style is applied, since we can't be more specific
    visibility: "visible !important",
  },
});

export interface NoRowsOverlayProps {
  /** Message to show when there is an error loading the data */
  errorMessage: string;

  /** Whether to show error message or not */
  shouldShowErrorMessage: boolean;
}

/** Content to show when there are no data available */
export function NoRowsOverlay({
  errorMessage,
  shouldShowErrorMessage,
}: NoRowsOverlayProps): React.JSX.Element {
  return (
    <GridOverlay>
      <Text variant="bodyL">
        {shouldShowErrorMessage ? (
          <>
            <b>Error</b>: {errorMessage}
          </>
        ) : (
          "No data available."
        )}
      </Text>
    </GridOverlay>
  );
}
interface DataGridCellProps {
  /** The content to display inside the cell */
  content: React.ReactNode;

  /** The tooltip text to display when hovering over the cell */
  tooltip?: React.ReactNode;
}

/** A simple wrapper for DataGrid cell making sure the text style is consistent */
export function DataGridCell({
  content,
  tooltip,
}: DataGridCellProps): React.JSX.Element {
  return (
    <Tooltip
      title={tooltip ?? (typeof content === "string" ? content : "")}
      placement="bottom-end"
      enterDelay={500}
    >
      <Text sx={{ ...withEllipsisMultiline({ lines: 1 }) }} variant="bodyM">
        {content}
      </Text>
    </Tooltip>
  );
}

interface DataGridDateCellProps {
  /** The date string to display in the cell */
  date: DateTimeInput;

  /** Whether to show full date time or not. It will override the global setting for showing full date time in data grid. */
  shouldShowFullDateTimeOverride?: boolean;
}

/** A wrapper for showing date in DataGrid cell */
export function DataGridDateCell({
  date,
  shouldShowFullDateTimeOverride = false,
}: DataGridDateCellProps): React.JSX.Element {
  const { shouldShowFullDateTime } = useAppSelector(devFeaturesStateSelector);

  return (
    <DataGridCell
      content={DateTimeUtils.formatDateTimeInUtc({
        date,
        shouldShowFullDateTime:
          shouldShowFullDateTimeOverride || shouldShowFullDateTime,
        shouldShowUtcText: false,
      })}
    />
  );
}

/** Extract the entity from data-grid cell params */
export function getEntity<T>(params: EntityCellParams<T>): T {
  return params.row.entity;
}
